import React from 'react'
import { useHistory } from 'react-router-dom'

import { Formik } from 'formik'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Form, Col, Row, Divider, Button } from '_zari/core'

import CondicionesPagoConstant from 'constants/condicionesDePago'
import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import TiposDeDocumentoSunatConstant from 'constants/tiposDeDocumentoSunat'
import ListaDeMonedasConstant from 'constants/listaDeMonedas'
import { TiposTablaComunConstant } from 'constants/index'
import AfectacionesCompraConstant from 'constants/afectacionesCompra'

import { ZrSwal, IdGen, RounderUtil } from 'utils'

import * as CompraCrud from 'services/compra.service'
import * as TablaComunService from 'services/tablasComunes.service'

import { RetrocederButton } from 'sharedComponents/buttons'
import { NuevoActualizarProductoModal } from 'sharedComponents/modals'

import * as FormGroups from './formGroups'
import * as Sections from './sections'
import * as Modals from './modals'

import { ValidationSchema } from './validation'

const actualizarFila = (current, { cantidad, precio, subTotal, flete, isc, __updatedField, ...restPayload }) => {
  current = {
    ...current,
    ...restPayload,
    cantidad,
    precio,
    subTotal,
    flete,
    isc
  }

  if (__updatedField === 'cantidad') {
    // Si la fila viene de una OC y ya tiene precio, conservar precio y recalcular subTotal/total/igv.
    // Si es una fila manual, limpiar para que el usuario ingrese subTotal.
    const precioConocido =
      current._fromOrdenCompra && current.precio != null && current.precio !== '' && parseFloat(current.precio) > 0

    if (precioConocido) {
      const numCantidad = parseFloat(cantidad || '0')
      const numPrecio = parseFloat(current.precio || '0')
      const numSubTotal = RounderUtil.round(numCantidad * numPrecio)
      const numIsc = parseFloat(isc || '0')
      const numTotal = numSubTotal + numIsc
      let numIgv = 0
      if (
        current.afectacion !== AfectacionesCompraConstant.NO_GRAVADO &&
        current.afectacion !== AfectacionesCompraConstant.OTROS
      ) {
        numIgv = RounderUtil.round(numTotal * 0.18)
      }
      current.subTotal = numSubTotal.toFixed(2)
      current.total = numTotal.toFixed(2)
      current.igv = numIgv
      // precio se conserva
    } else {
      current.precio = ''
      current.subTotal = ''
      current.total = ''
      current.igv = 0
    }
  } else if (__updatedField === 'subtotal') {
    const numCantidad = parseFloat(cantidad || '0')
    const numSubTotal = parseFloat(subTotal || '0')
    const numIsc = parseFloat(isc || '0')
    const numPrecio = numCantidad !== 0 ? numSubTotal / numCantidad : 0
    const numTotal = numSubTotal + numIsc
    let numIgv = 0

    if (
      current.afectacion !== AfectacionesCompraConstant.NO_GRAVADO &&
      current.afectacion !== AfectacionesCompraConstant.OTROS
    ) {
      numIgv = RounderUtil.round(numTotal * 0.18)
    }

    current.igv = numIgv
    current.precio = numPrecio.toFixed(2)
    current.total = numTotal.toFixed(2)
  } else if (__updatedField === 'menos-igv') {
    const numCantidad = parseFloat(cantidad || '0')
    const numPrevSubTotal = parseFloat(subTotal || '0')
    const numSubTotal = RounderUtil.round(parseFloat(subTotal || '0') / 1.18)
    const numIsc = parseFloat(isc || '0')
    const numPrecio = numCantidad !== 0 ? numSubTotal / numCantidad : 0
    const numTotal = numSubTotal + numIsc
    let numIgv = 0

    if (
      current.afectacion !== AfectacionesCompraConstant.NO_GRAVADO &&
      current.afectacion !== AfectacionesCompraConstant.OTROS
    ) {
      numIgv = RounderUtil.round(numPrevSubTotal - numSubTotal)
    }

    current.subTotal = numSubTotal
    current.igv = numIgv
    current.precio = numPrecio.toFixed(2)
    current.total = numTotal.toFixed(2)
  } else if (__updatedField === 'flete' || __updatedField === 'isc') {
    const numSubTotal = parseFloat(subTotal || '0')
    const numIsc = parseFloat(isc || '0')
    const numTotal = numSubTotal + numIsc

    let numIgv = 0

    if (
      current.afectacion !== AfectacionesCompraConstant.NO_GRAVADO &&
      current.afectacion !== AfectacionesCompraConstant.OTROS
    ) {
      numIgv = RounderUtil.round(numTotal * 0.18)
    }

    current.igv = numIgv
    current.total = numTotal.toFixed(2)
  }
  return current
}

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      let newState = state
        .map(val => ({
          ...val,
          __filaActiva: false,
          __focusCantidad: false,
          __focusAfectacion: false,
          __focusPrecio: false,
          __focusSubTotal: false,
          __focusIsc: false,
          __focusFlete: false,
          esFilaBusqueda: false
        }))
        .filter(x => x._idProductoPresentacion)

      let { nuevaFila, viaCodBar, ...payload } = action.payload

      return [
        ...newState,
        {
          ...payload,
          __filaActiva: !viaCodBar,
          __focusCantidad: !viaCodBar,
          _idFila: Math.round(new Date() / 1000),
          cantidad: '',
          precio: '',
          subTotal: '',
          total: '',
          flete: '',
          isc: ''
        },
        {
          __filaActiva: viaCodBar,
          esFilaBusqueda: true,
          _idFila: `${Math.round(new Date() / 1000)}-G`
        }
      ]
    }
    case 'actualizar': {
      const { saltarFila, ...nuevoPayload } = action.payload
      return state.map(val => {
        if (val.esFilaBusqueda) {
          val.__filaActiva = saltarFila
        } else {
          if (val._idFila === nuevoPayload._idFila) {
            val = actualizarFila(val, nuevoPayload)
          }
          val.__filaActiva = !saltarFila
        }
        return val
      })
    }
    case 'actualizarOtros':
      return state.map((val, i) => {
        if (val._idFila === action.payload._idFila) {
          val = { ...val }
          val.precioReferencia = action.payload.precioReferencia
          val.afectacion = action.payload.afectacion
          // Recalcular IGV al cambiar afectación
          const numSubTotal = parseFloat(val.subTotal || '0')
          const numIsc = parseFloat(val.isc || '0')
          const numTotal = numSubTotal + numIsc
          let numIgv = 0
          if (
            val.afectacion !== AfectacionesCompraConstant.NO_GRAVADO &&
            val.afectacion !== AfectacionesCompraConstant.OTROS
          ) {
            numIgv = RounderUtil.round(numTotal * 0.18)
          }
          val.igv = numIgv
          val.total = numTotal.toFixed(2)
        }
        return val
      })
    case 'restarIgv':
      return state.map(val => {
        // Las filas provenientes de OC ya traen el subTotal sin IGV (viene ya limpio).
        // No volver a dividir entre 1.18 en esas filas, porque distorsiona los montos.
        if (!val.esFilaBusqueda && !val._fromOrdenCompra) {
          val = actualizarFila(val, {
            ...val,
            __updatedField: 'menos-igv'
          })
        }
        return val
      })
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    case 'fill': {
      return action.payload
    }
    case 'append': {
      const busqueda = state.filter(x => x.esFilaBusqueda)
      const noBusqueda = state.filter(x => !x.esFilaBusqueda)
      return [...noBusqueda, ...action.payload, ...busqueda]
    }
    case 'removeByOrdenCompra': {
      return state.filter(x => x.esFilaBusqueda || x._idOrdenCompra !== action.payload._idOrdenCompra)
    }
    default:
      return state
  }
}

const NuevaMainForm = ({ editarData, flagPorGuias, codigoTransaccion }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const history = useHistory()

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, editarData.detalles, innerArr => {
    let output = []

    if (innerArr) {
      output = innerArr.map((x, k) => ({
        ...x,
        _idFila: k
      }))
    }

    // if (flagPorGuias) {
    //   return []
    // }

    output.push({
      esFilaBusqueda: true,
      _idFila: IdGen.newId()
    })

    return output
  })

  const mainFormikRef = React.useRef({})
  const mainFormDataRef = React.useRef({})
  const detallesGuiasRef = React.useRef([])
  const montosFormRef = React.useRef(null)

  const [__detallesKey, setDetallesKey] = React.useState(IdGen.newId())

  const [afectacionesCompra, setAfectacionesCompra] = React.useState([])
  const [mostrarRefrescar, setMostrarRefrescar] = React.useState(false)
  const [mostrarFormularioPagosModal, setMostrarFormularioPagosModal] = React.useState(false)
  const [mostrarConsultarDocumentoModal, setMostrarConsultarDocumentoModal] = React.useState(false)
  const [mostrarListarGuiasModal, setMostrarListarGuiasModal] = React.useState(false)
  const [mostrarListarOrdenes, setMostrarListarOrdenes] = React.useState(false)
  const [mostrarConsumoDirectoModal, setMostrarConsumoDirectoModal] = React.useState(false)
  const [mostrarNuevoActualizarProductoModal, setMostrarNuevoActualizarProductoModal] = React.useState(false)
  const [ordenesSeleccionadasInfo, setOrdenesSeleccionadasInfo] = React.useState({})

  const registrarOrdenSeleccionada = React.useCallback((idOC, info) => {
    setOrdenesSeleccionadasInfo(prev => ({ ...prev, [idOC]: info }))
  }, [])

  const removerOrdenCompra = React.useCallback(
    (idOC, values, setValues) => {
      const nuevosIds = (values._idsOrdenCompra || []).filter(x => x !== idOC)
      setValues({
        ...values,
        _idsOrdenCompra: nuevosIds,
        _idOrdenCompra: nuevosIds.length === 1 ? nuevosIds[0] : nuevosIds.length === 0 ? '' : values._idOrdenCompra
      })
      detallesDispatcher({ type: 'removeByOrdenCompra', payload: { _idOrdenCompra: idOC } })
      setDetallesKey(IdGen.newId())
      setOrdenesSeleccionadasInfo(prev => {
        const { [idOC]: _omit, ...rest } = prev
        return rest
      })
    },
    []
  )

  React.useEffect(() => {
    TablaComunService.getAsSelect(TiposTablaComunConstant.AFECTACIONCOMPRA)
      .then(({ data }) => {
        setAfectacionesCompra(data)
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext])

  const formInitialValues = React.useMemo(
    () => ({
      _idModeloCuentas12: editarData._idModeloCuentas12 || '',
      _idSocio: editarData._idSocio ?? '',
      codigoSocio: editarData.codigoSocio ?? '',
      codigoTransaccion: codigoTransaccion,
      comentario: editarData.comentario ?? '',
      condicionPago: editarData.condicionPago ?? CondicionesPagoConstant.CREDITO,
      documentoSocio: editarData.documentoSocio ?? '',
      fechaAlmacenamiento: editarData.fechaAlmacenamiento ?? '',
      fechaEmision: editarData.fechaEmision ?? '',
      fechaVencimiento: editarData.fechaVencimiento ?? '',
      flagConsumoDirecto: false,
      flagPercepcion: editarData.flagPercepcion ?? false,
      flagPorGuias: flagPorGuias,
      glosa: editarData.glosa ?? '',
      idsGuias: editarData.idsGuias ?? [],
      incluyeIGV: editarData.incluyeIGV ?? false,
      moneda: editarData.moneda ?? ListaDeMonedasConstant.SOLES,
      montoPercepcion: editarData.montoPercepcion ?? 0,
      montoRedondeoIgv: editarData.montoRedondeoIgv ?? '',
      nombreSocio: editarData.nombreSocio ?? '',
      numeroDocumento: editarData.numeroDocumento ?? '',
      serieDocumento: editarData.serieDocumento ?? '',
      tipoCambio: editarData.tipoCambio ?? '',
      tipoDocumento: editarData.tipoDocumento ?? TiposDeDocumentoSunatConstant.FACTURA,
      tipoDocumentoSocio: editarData.tipoDocumentoSocio ?? '',
      guiaRemision: editarData.guiaRemision ?? '',
      _idOrdenCompra: editarData._idOrdenCompra ?? '',
      _idsOrdenCompra: editarData._idsOrdenCompra ?? (editarData._idOrdenCompra ? [editarData._idOrdenCompra] : [])
    }),
    [codigoTransaccion, editarData, flagPorGuias]
  )

  const clickRefrescarHandler = React.useCallback(
    e => {
      editarData.sumaIgv = 0
      montosFormRef.current = null
      setMostrarRefrescar(false)
    },
    [editarData, montosFormRef]
  )

  const clickListarGuias = React.useCallback(() => {
    setMostrarListarGuiasModal(true)
  }, [])

  const clickListarOrdenes = React.useCallback(() => {
    setMostrarListarOrdenes(true)
  }, [])

  const submitFunction = React.useCallback(
    (submitData, setSubmitting, idEdicion) => {
      const promiseSubmit = idEdicion
        ? () => CompraCrud.actualizarCompra(idEdicion, submitData)
        : () => CompraCrud.nuevaCompra(submitData)

      promiseSubmit()
        .then(({ data: data2 }) => {
          ZrSwal.showAlert({
            tipo: 'success',
            mensaje: (
              <>
                <span>Operaciones Generadas :</span> <br />
                {data2.operacionesGeneradas.map((x, k) => (
                  <React.Fragment key={k}>
                    <span>
                      {k + 1}. {x}
                    </span>{' '}
                    <br />
                  </React.Fragment>
                ))}
              </>
            ),
            onSuccess: () => {
              if (flagPorGuias) {
                history.push(`/${CodigosTransaccionesConstant.COMPRASGUIAS_LISTA}`)
              } else {
                history.push(`/${CodigosTransaccionesConstant.COMPRAS_LISTA}`)
              }
            }
          })
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          if (setSubmitting) {
            setSubmitting(false)
          }
        })
    },
    [__zariContext, history, flagPorGuias]
  )

  const submitHandler = (data, { setSubmitting }) => {
    if (
      data.tipoDocumento === TiposDeDocumentoSunatConstant.NOTA_CREDITO ||
      data.tipoDocumento === TiposDeDocumentoSunatConstant.NOTA_DEBITO
    ) {
      if (data.condicionPago !== CondicionesPagoConstant.CREDITO) {
        __zariContext.snack('error', 'El tipo de documento seleccionado solo admite CREDITO como medio de pago.')
        setSubmitting(false)
        return
      }
    }

    const detallesNormalizados = detalles
      .filter(x => !x.esFilaBusqueda)
      .map((x, k) => {
        let importeIgv = x.igv

        const flagUltimo = k === detalles.length - 2
        if (flagUltimo && data.montoRedondeoIgv) {
          importeIgv = RounderUtil.round(importeIgv + parseFloat(data.montoRedondeoIgv || 0))
        }

        return {
          _idProductoPresentacion: x._idProductoPresentacion,
          _idOrdenCompraDetalle: x._idDetalleOrden || x._idOrdenCompraDetalle || undefined,
          _idOrdenCompra: x._idOrdenCompra || undefined,
          afectacion: x.afectacion,
          cantidad: parseFloat(x.cantidad || '0'),
          codigoProducto: x.codigoProducto,
          flete: parseFloat(x.flete || '0'),
          igv: importeIgv,
          isc: parseFloat(x.isc || '0'),
          nombreProducto: x.nombreProducto,
          precio: parseFloat(x.precio || '0'),
          subTotal: parseFloat(x.subTotal || '0'),
          total: parseFloat(x.total || '0')
        }
      })

    mainFormDataRef.current = {
      ...data,
      numeroDocumento: parseFloat(data.numeroDocumento),
      serieDocumento: data.serieDocumento.toUpperCase(),
      ...montosFormRef.current.values,
      detalles: detallesNormalizados
    }

    if (data.condicionPago === CondicionesPagoConstant.CREDITO) {
      if (
        data.tipoDocumento === TiposDeDocumentoSunatConstant.NOTA_CREDITO ||
        data.tipoDocumento === TiposDeDocumentoSunatConstant.NOTA_DEBITO
      ) {
        setMostrarConsultarDocumentoModal(true)
      } else {
        setMostrarConsumoDirectoModal(true)
      }
    } else {
      setMostrarConsumoDirectoModal(true)
    }
  }

  const guardarConsumoDirectoFunction = React.useCallback(
    nuevoMainForm => {
      setMostrarConsumoDirectoModal(false)
      mainFormDataRef.current = nuevoMainForm

      if (nuevoMainForm.condicionPago === CondicionesPagoConstant.CREDITO) {
        submitFunction(mainFormDataRef.current, null, editarData._id)
      } else {
        setMostrarFormularioPagosModal(true)
      }
    },
    [submitFunction, editarData]
  )

  const clickMenosIgv = React.useCallback(() => {
    detallesDispatcher({ type: 'restarIgv' })
    setDetallesKey(IdGen.newId())
  }, [])

  const clickNuevoActualizarProducto = React.useCallback(() => {
    setMostrarNuevoActualizarProductoModal(true)
  }, [])

  return (
    <>
      <Row>
        <Col md={12}>
          <Formik
            initialValues={formInitialValues}
            onSubmit={submitHandler}
            innerRef={mainFormikRef}
            validationSchema={ValidationSchema}
            validateOnMount={false}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ errors, isSubmitting, setValues, touched, values, handleChange, handleSubmit, setFieldValue }) => (
              <>
                <Form>
                  <Row>
                    <Col md={4}>
                      <FormGroups.SocioFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors._idSocio}
                        values={values}
                        setValues={setValues}
                        vSocio={values._idSocio}
                      />
                      {values._idSocio !== '' && (
                        <>
                          {flagPorGuias === true ? (
                            <Row>
                              <Col md='12'>
                                <Button block onClick={clickListarGuias}>
                                  Mostrar Guias
                                </Button>
                              </Col>
                            </Row>
                          ) : (
                            <Row>
                              <Col md='12'>
                                <Button block onClick={clickListarOrdenes}>
                                  {(values._idsOrdenCompra || []).length > 0 ? 'Agregar Orden' : 'Mostrar Ordenes'}
                                </Button>
                                {(values._idsOrdenCompra || []).length > 0 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexWrap: 'wrap',
                                      gap: '4px',
                                      marginTop: '6px'
                                    }}
                                  >
                                    {(values._idsOrdenCompra || []).filter(Boolean).map(idOC => {
                                      const info = ordenesSeleccionadasInfo[idOC]
                                      const idOCStr = typeof idOC === 'string' ? idOC : String(idOC || '')
                                      const label = info
                                        ? `${info.serieDocumento || ''}-${info.numeroDocumento || ''}`
                                        : idOCStr.substring(0, 6)
                                      return (
                                        <span
                                          key={idOC}
                                          style={{
                                            backgroundColor: '#E6ECFF',
                                            color: '#1F3A8A',
                                            padding: '3px 8px',
                                            borderRadius: '12px',
                                            fontSize: '0.75rem',
                                            fontWeight: 'bold',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                          }}
                                          title='Click × para quitar esta OC'
                                        >
                                          OC {label}
                                          <button
                                            type='button'
                                            onClick={() => removerOrdenCompra(idOC, values, setValues)}
                                            style={{
                                              border: 'none',
                                              background: 'transparent',
                                              color: '#dc3545',
                                              cursor: 'pointer',
                                              fontSize: '0.9rem',
                                              padding: 0,
                                              lineHeight: 1
                                            }}
                                          >
                                            &times;
                                          </button>
                                        </span>
                                      )
                                    })}
                                  </div>
                                )}
                              </Col>
                            </Row>
                          )}
                        </>
                      )}
                      <FormGroups.GlosaFormGroup
                        iError={errors.glosa}
                        iTouched={touched.glosa}
                        fIsSubmitting={isSubmitting}
                      />
                    </Col>
                    <Col md={3}>
                      <FormGroups.TipoDocumentoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.tipoDocumento}
                        iTouched={touched.tipoDocumento}
                      />
                      <FormGroups.SerieNumeroFormGroup
                        fIsSubmitting={isSubmitting}
                        iErrorSerie={errors.serieDocumento}
                        iTouchedSerie={touched.serieDocumento}
                        iErrorNumero={errors.numeroDocumento}
                        iTouchedNumero={touched.numeroDocumento}
                      />
                      <FormGroups.MonedaFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.moneda}
                        iTouched={touched.moneda}
                      />
                    </Col>
                    <Col md={3}>
                      <FormGroups.FechaEmisionFormGroup
                        handleChange={handleChange}
                        values={values}
                        setValues={setValues}
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaEmision}
                        iTouched={touched.fechaEmision}
                      />
                      {/* <FormGroups.FechaAlmacenamientoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaAlmacenamiento}
                        iTouched={touched.fechaAlmacenamiento}
                      /> */}
                      <FormGroups.FechaVencimientoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.fechaVencimiento}
                        iTouched={touched.fechaVencimiento}
                      />
                    </Col>
                    <Col md={2}>
                      <FormGroups.TipoCambioFormGroup
                        iError={errors.tipoCambio}
                        iTouched={touched.tipoCambio}
                        fIsSubmitting={isSubmitting}
                        iMoneda={values.moneda}
                        iFechaDocumento={values.fechaEmision}
                        setFieldValue={setFieldValue}
                      />
                      {__adminContext.valoresConfiguracionCompania.flagContabilidad && (
                        <FormGroups.ModelosCuentas12FormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors._idModeloCuentas12}
                          iTouched={touched._idModeloCuentas12}
                          setFieldValue={setFieldValue}
                          vCodigoTransaccion={values.codigoTransaccion}
                        />
                      )}
                    </Col>
                  </Row>
                  <Divider />
                  <Row>
                    <Col md={6} className='mb-2'>
                      <Button onClick={clickNuevoActualizarProducto} tiny outline>
                        Nuevo producto
                      </Button>
                    </Col>
                    <Col md={6} className='text-right mb-2'>
                      <Button onClick={clickMenosIgv} tiny outline>
                        Restar IGV
                      </Button>
                    </Col>
                  </Row>
                  <Sections.DetallesSection
                    key={__detallesKey}
                    detalles={detalles}
                    dispatch={detallesDispatcher}
                    incluyeIGV={false}
                    setMostrarRefrescar={setMostrarRefrescar}
                    afectacionesCompra={afectacionesCompra}
                  />
                  <Divider />
                  {mostrarRefrescar && (
                    <Row className='mb-2'>
                      <Col md='2' className='offset-md-10'>
                        <Button block type='button' variant='primary' size='sm' onClick={clickRefrescarHandler}>
                          Recalcular
                        </Button>
                      </Col>
                    </Row>
                  )}
                  <Row className='mt-2'>
                    <Col md={5}>
                      <FormGroups.ComentarioFormGroup
                        iError={errors.comentario}
                        iTouched={touched.comentario}
                        fIsSubmitting={isSubmitting}
                      />
                      <FormGroups.GuiaRemisionFormGroup
                        iError={errors.guiaRemision}
                        iTouched={touched.guiaRemision}
                        fIsSubmitting={isSubmitting}
                      />
                    </Col>
                    <Col md={7}>
                      <Sections.ResumenMontosSection
                        montosFormRef={montosFormRef}
                        detalles={detalles.filter(x => !x.esFilaBusqueda)}
                        montoPercepcionValue={editarData.montoPercepcion}
                        flagPercepcion={mostrarRefrescar ? false : values.flagPercepcion}
                        setOutFieldValue={setFieldValue}
                        importeIgvValue={editarData.sumaIgv}
                      />
                    </Col>
                  </Row>
                  <Divider />
                  <Row>
                    <Col md={10} />
                    <Col md={2}>
                      <FormGroups.CondicionPagoFormGroup
                        iError={errors.condicionPago}
                        iTouched={touched.condicionPago}
                        fIsSubmitting={isSubmitting}
                      />
                      {__adminContext.valoresConfiguracionCompania ? (
                        <>
                          {/* Consumo directo siempre activo, checkbox oculto */}
                        </>
                      ) : null}
                    </Col>
                  </Row>
                  {!mostrarRefrescar && (
                    <Row>
                      <Col md={12} className='text-right'>
                        <RetrocederButton />
                        <Button disabled={isSubmitting || mostrarRefrescar} className='ml-2' onClick={handleSubmit}>
                          Continuar
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Form>
              </>
            )}
          </Formik>
        </Col>
      </Row>
      {mostrarFormularioPagosModal && (
        <Modals.FormularioPagosModal
          handleClose={e => {
            setMostrarFormularioPagosModal(false)
            mainFormikRef.current.setSubmitting(false)
          }}
          show={mostrarFormularioPagosModal}
          history={history}
          mainFormData={mainFormDataRef.current}
          idEdicion={editarData._id}
          submitFunction={submitFunction}
        />
      )}
      {mostrarConsultarDocumentoModal && (
        <Modals.ConsultarDocumentoModal
          handleClose={e => {
            setMostrarConsultarDocumentoModal(false)
            mainFormikRef.current.setSubmitting(false)
          }}
          show={mostrarConsultarDocumentoModal}
          history={history}
          mainFormData={mainFormDataRef.current}
          submitFunction={submitFunction}
        />
      )}
      {mostrarListarGuiasModal && (
        <Modals.ListarGuiasModal
          mostrarRefrescar={values => {
            montosFormRef.current.values = values
          }}
          handleClose={e => setMostrarListarGuiasModal(false)}
          show={mostrarListarGuiasModal}
          mainFormData={mainFormDataRef.current}
          idSocio={mainFormikRef.current.values._idSocio}
          setValues={mainFormikRef.current.setValues}
          values={mainFormikRef.current.values}
          dispatch={detallesDispatcher}
          detallesGuiasRef={detallesGuiasRef}
        />
      )}
      {mostrarListarOrdenes && (
        <Modals.ListaOrdenesModal
          mostrarRefrescar={values => {
            montosFormRef.current = null
          }}
          handleClose={e => setMostrarListarOrdenes(false)}
          show={mostrarListarOrdenes}
          mainFormData={mainFormDataRef.current}
          idSocio={mainFormikRef.current.values._idSocio}
          setValues={mainFormikRef.current.setValues}
          values={mainFormikRef.current.values}
          dispatch={detallesDispatcher}
          detallesGuiasRef={detallesGuiasRef}
          idsOCsYaSeleccionadas={mainFormikRef.current.values._idsOrdenCompra || []}
          registrarOrdenSeleccionada={registrarOrdenSeleccionada}
        />
      )}
      {mostrarConsumoDirectoModal && (
        <Modals.ConsumoDirectoModal
          handleClose={e => {
            setMostrarConsumoDirectoModal(false)
            mainFormikRef.current.setSubmitting(false)
          }}
          show={mostrarConsumoDirectoModal}
          mainFormData={mainFormDataRef.current}
          guardarConsumoDirectoFunction={guardarConsumoDirectoFunction}
        />
      )}
      {mostrarNuevoActualizarProductoModal && (
        <NuevoActualizarProductoModal
          show={mostrarNuevoActualizarProductoModal}
          handleClose={() => setMostrarNuevoActualizarProductoModal(false)}
        />
      )}
    </>
  )
}

export default NuevaMainForm
